/*
 * Copyright (C) 2018 - present by OpenGamma Inc. and the OpenGamma group of companies
 *
 * Please see distribution for license.
 */

import { map, mergeMap, tap } from 'rxjs/operators';
import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Observable } from 'rxjs';
import { SnackBarService } from 'app/shared/services/snack-bar-service';
import { Action } from '@ngrx/store';
import { Router } from '@angular/router';
import {
  AddReadOnlyRoleSuccess,
  ADD_READ_ONLY_ROLE_SUCCESS,
  AddReadOnlyRoleError,
  ADD_READ_ONLY_ROLE_ERROR,
  ADD_USER_FOR_TENANT_ERROR,
  ADD_USER_FOR_TENANT_SUCCESS,
  AddUserForTenantError,
  CREATE_TENANT_DETAILS_SUCCESS,
  DELETE_SELECTED_TENANT_DETAILS_SUCCESS,
  DELETE_USER_FOR_TENANT_ERROR,
  DELETE_USER_FOR_TENANT_SUCCESS,
  DeleteUserForTenantError,
  LOAD_TENANT_DETAILS_SUCCESS,
  LoadUsersForTenant,
  UPDATE_TENANT_DETAILS_ERROR,
  UPDATE_TENANT_DETAILS_SUCCESS,
  UPDATE_USER_FOR_TENANT_SUCCESS,
  UpdateTenantDetailsError,
  UpdateTenantDetailsSuccess,
  CreateTenantDetailsSuccess,
  LoadTenantDetailsSuccess
} from './api/tenant-management-api.actions';
import { SELECT_TENANT_DETAILS, HideUserCreationWizard } from './tenant-management.actions';
import { LoadAllStaticDataValues } from 'app/static-data/store/static-data.actions';

@Injectable()
export class TenantManagementEffects {
  /** Load all tenant details API trigger. */

  sendUpdateSuccessSnackbarMessage: Observable<Action> = createEffect(
    () =>
      this.actions.pipe(
        ofType<UpdateTenantDetailsSuccess>(UPDATE_TENANT_DETAILS_SUCCESS),
        tap((action: UpdateTenantDetailsSuccess) =>
          this.snackbarService.sendNotification({ success: true, message: action.successMessage })
        )
      ),
    { dispatch: false }
  );

  /** Triggers a notification when tenant details are updated unsuccessfully. */

  sendUpdateErrorSnackbarMessage: Observable<Action> = createEffect(
    () =>
      this.actions.pipe(
        ofType<UpdateTenantDetailsError>(UPDATE_TENANT_DETAILS_ERROR),
        tap((action: UpdateTenantDetailsError) =>
          this.snackbarService.sendNotification({ success: false, message: action.errorMessage })
        )
      ),
    { dispatch: false }
  );

  /** Add read-only tenant role API trigger. */

  sendAddReadOnlyRoleSuccessSnackbarMessage: Observable<Action> = createEffect(
    () =>
      this.actions.pipe(
        ofType<AddReadOnlyRoleSuccess>(ADD_READ_ONLY_ROLE_SUCCESS),
        tap((action: AddReadOnlyRoleSuccess) =>
          this.snackbarService.sendNotification({
            success: true,
            message: 'Read-only role added successfully'
          })
        )
      ),
    { dispatch: false }
  );

  /** Triggers a notification when tenant's read-only role is added unsuccessfully. */

  sendAddReadOnlyRoleErrorSnackbarMessage: Observable<Action> = createEffect(
    () =>
      this.actions.pipe(
        ofType<AddReadOnlyRoleError>(ADD_READ_ONLY_ROLE_ERROR),
        tap((action: AddReadOnlyRoleError) =>
          this.snackbarService.sendNotification({ success: false, message: action.errorMessage })
        )
      ),
    { dispatch: false }
  );

  /** Triggers a notification and hides the new user panel when a user profile is created successfully. */

  newUserSuccessfullyAdded: Observable<Action> = createEffect(() =>
    this.actions.pipe(
      ofType(ADD_USER_FOR_TENANT_SUCCESS),
      tap(() =>
        this.snackbarService.sendNotification({
          success: true,
          message: 'User created successfully!'
        })
      ),
      map(() => new HideUserCreationWizard())
    )
  );

  /** Triggers a notification when a user profile cannot be saved. */

  newUserErrorSnackbarMessage: Observable<Action> = createEffect(
    () =>
      this.actions.pipe(
        ofType(ADD_USER_FOR_TENANT_ERROR),
        tap((action: AddUserForTenantError) =>
          this.snackbarService.sendNotification({ success: false, message: action.errorMessage })
        )
      ),
    { dispatch: false }
  );

  /** Triggers a notification when user details are updated successfully. */

  userDetailsUpdateSuccess: Observable<Action> = createEffect(
    () =>
      this.actions.pipe(
        ofType(UPDATE_USER_FOR_TENANT_SUCCESS),
        tap(() =>
          this.snackbarService.sendNotification({
            success: true,
            message: 'User updated successfully!'
          })
        )
      ),
    { dispatch: false }
  );

  /** User deleted successfully. */

  userDeletionSuccess: Observable<Action> = createEffect(
    () =>
      this.actions.pipe(
        ofType(DELETE_USER_FOR_TENANT_SUCCESS),
        tap(() =>
          this.snackbarService.sendNotification({
            success: true,
            message: 'User deleted successfully!'
          })
        )
      ),
    { dispatch: false }
  );

  /** User could not be deleted. */

  userDeletionError: Observable<Action> = createEffect(
    () =>
      this.actions.pipe(
        ofType(DELETE_USER_FOR_TENANT_ERROR),
        tap((action: DeleteUserForTenantError) =>
          this.snackbarService.sendNotification({ success: false, message: action.errorMessage })
        )
      ),
    { dispatch: false }
  );

  /** Tenant created successfully. */

  tenantCreatedSuccessfully: Observable<Action> = createEffect(
    () =>
      this.actions.pipe(
        ofType(CREATE_TENANT_DETAILS_SUCCESS),
        tap((action: CreateTenantDetailsSuccess) => {
          this.snackbarService.sendNotification({
            success: true,
            message: 'Tenant Created Successfully!'
          });
          this.router.navigate([
            '/tenants',
            action.tenantDetails.organizationId,
            'details',
            'provisioning'
          ]);
        })
      ),
    { dispatch: false }
  );

  /** Tenant deleted successfully. */

  tenantDeletedSuccessfully: Observable<Action> = createEffect(
    () =>
      this.actions.pipe(
        ofType(DELETE_SELECTED_TENANT_DETAILS_SUCCESS),
        tap(() => {
          this.snackbarService.sendNotification({
            success: true,
            message: 'Tenant Deleted Successfully!'
          });
          this.router.navigateByUrl('/tenants');
        })
      ),
    { dispatch: false }
  );

  /** Load all users for tenant. */

  loadUsersOnTenantSelection: Observable<Action> = createEffect(() =>
    this.actions.pipe(
      ofType(SELECT_TENANT_DETAILS, LOAD_TENANT_DETAILS_SUCCESS),
      mergeMap(() => [new LoadUsersForTenant(), new LoadAllStaticDataValues()])
    )
  );

  /** Move the user to the tenant's provisioning page if still provisioning. */

  openProvisioningPageIfProvisioning: Observable<Action> = createEffect(
    () =>
      this.actions.pipe(
        ofType(LOAD_TENANT_DETAILS_SUCCESS),
        tap((action: LoadTenantDetailsSuccess) => {
          if (action.tenantDetailsWithFlags.data.status == 'provisioning') {
            this.router.navigate([
              '/tenants',
              action.tenantDetailsWithFlags.data.organizationId,
              'details',
              'provisioning'
            ]);
            this.snackbarService.sendNotification({
              message: 'Tenant provisioning in progress.',
              success: true
            });
          }
        })
      ),
    { dispatch: false }
  );

  constructor(
    private actions: Actions,
    private router: Router,
    private snackbarService: SnackBarService
  ) {}
}
